/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState } from 'react';
import chunk from 'lodash/chunk';
import sortBy from 'lodash/sortBy';
import {
    Pagination,
    Heading,
    ContentContainerBodyShift,
    FlexRow,
    FlexCol,
    formatNumber,
} from 'sarsaparilla';
import UpcomingLotteryCard from './UpcomingLotteryCard';
import { Lottery } from '../types/lottery';

const pageSize = 3;

interface UpcomingLotteriesProps {
    lotteries?: Lottery[];
}

function UpcomingLotteries({ lotteries }: UpcomingLotteriesProps) {
    const [page, setPage] = useState(0);

    const renderLotteries = (count: number, currentPage: number, pages: Lottery[][]) => {
        if (count === 0) {
            return (
                <section className="lottery-block">
                    <FlexRow className="lottery-no-results-wrap">
                        <FlexCol mdOffset={2} md={8}>
                            <p>
                                Checking out what&apos;s coming soon is a great way to
                                plan ahead, but there are currently no lotteries scheduled
                                to open at this time.
                            </p>
                        </FlexCol>
                    </FlexRow>
                </section>
            );
        }

        const lowRange = pageSize * currentPage + 1;
        const highRange =
            currentPage === 0 && count < pageSize ? count : pageSize * currentPage + 3;

        return (
            <>
                <section>
                    <FlexRow hasGutters={false}>
                        {pages[currentPage].map((lottery: Lottery) => (
                            <UpcomingLotteryCard key={lottery.id} lottery={lottery} />
                        ))}
                    </FlexRow>
                </section>

                <FlexRow
                    justifyContent="space-between"
                    alignItems="center"
                    className="lottery-pagination-wrap"
                >
                    <FlexCol xs={4} sm={8}>
                        <p className="lottery-results-text">
                            {formatNumber(lowRange, 0, true)}&ndash;
                            {formatNumber(highRange, 0, true)} of{' '}
                            {formatNumber(count, 0, true)} results
                        </p>
                    </FlexCol>
                    <FlexCol xs={8} sm={4} className="lottery-pagination">
                        <Pagination
                            ariaLabel="Coming soon navigation"
                            pages={pages.length}
                            activePage={currentPage}
                            onChange={setPage}
                        />
                    </FlexCol>
                </FlexRow>
            </>
        );
    };

    const pages: Lottery[][] = chunk(sortBy(lotteries, 'open_at'), pageSize);
    const count =
        pages.length === 0
            ? 0
            : (pages.length - 1) * pageSize + pages[pages.length - 1].length;

    return (
        <ContentContainerBodyShift>
            <FlexRow hasGutters={false}>
                <FlexCol>
                    <Heading
                        headingLevel={2}
                        hasUnderline="left"
                        appearance="h3"
                        className="lottery-section-header"
                    >
                        Coming Soon...
                    </Heading>
                </FlexCol>
            </FlexRow>
            {renderLotteries(count, page, pages)}
        </ContentContainerBodyShift>
    );
}

export default UpcomingLotteries;
